import { useState, useEffect, useCallback } from 'react';
import Kedo from '@/Kedo';
import { Block, UserStatisticsData } from '../types';

interface UseStatisticsDataOptions {
    kedo: Kedo;
    block: Block;
    endpoint: string;
    enabled?: boolean;
}

interface UseStatisticsDataResult<T> {
    data: T | null;
    loading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

export const useStatisticsData = <T>({
    kedo,
    endpoint,
    enabled = true,
}: UseStatisticsDataOptions): UseStatisticsDataResult<T> => {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchData = useCallback(async () => {
        const environment = kedo.env().getEnvironment();
        if (!environment || !enabled) return;

        setLoading(true);
        setError(null);

        try {
            const response = await kedo.api().post(endpoint, {
                environment: environment.id,
            });
            setData(response.data);
        } catch (err) {
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    }, [kedo, endpoint, enabled]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        loading,
        error,
        refetch: fetchData,
    };
};

export const useUserStatistics = (
    kedo: Kedo,
    block: Block,
    enabled = true
): UseStatisticsDataResult<UserStatisticsData> => {
    return useStatisticsData<UserStatisticsData>({
        kedo,
        block,
        endpoint: '/environmentadmin/statistics/user_current',
        enabled,
    });
};

export const useActiveUsersStatistics = (
    kedo: Kedo,
    block: Block,
    enabled = true
): UseStatisticsDataResult<unknown[]> => {
    return useStatisticsData<unknown[]>({
        kedo,
        block,
        endpoint: '/environmentadmin/statistics/user_activity',
        enabled,
    });
};

export interface UserMostActiveData {
    username?: string;
    user_id: number;
    total_count: number;
}

export const useUserMostActiveStatistics = (
    kedo: Kedo,
    block: Block,
    enabled = true
): UseStatisticsDataResult<UserMostActiveData[]> => {
    return useStatisticsData<UserMostActiveData[]>({
        kedo,
        block,
        endpoint: '/environmentadmin/statistics/users_most_active',
        enabled,
    });
};

interface DossierStatisticsData {
    thisWeek: Array<{ date: string; count: number }>;
    lastWeek: Array<{ date: string; count: number }>;
    twoWeeksAgo: Array<{ date: string; count: number }>;
}

export const useDossierStatistics = (
    kedo: Kedo,
    block: Block,
    enabled = true
): UseStatisticsDataResult<DossierStatisticsData> => {
    return useStatisticsData<DossierStatisticsData>({
        kedo,
        block,
        endpoint: '/environmentadmin/statistics/new_dossiers',
        enabled,
    });
};

interface UseSumFieldDataOptions {
    kedo: Kedo;
    block: Block;
    dossier?: { id: number };
    link?: number;
    enabled?: boolean;
}

interface UseSumFieldDataResult {
    data: number | string | null;
    loading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

export const useSumFieldData = ({
    kedo,
    block,
    dossier,
    link,
    enabled = true,
}: UseSumFieldDataOptions): UseSumFieldDataResult => {
    const [data, setData] = useState<number | string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchData = useCallback(async () => {
        const environment = kedo.env().getEnvironment();
        if (!environment || !enabled || !block.id) return;

        const api = kedo.api();
        const params: Record<string, unknown> = {};

        if (dossier?.id) {
            params.dossier = dossier.id;
            params.linkId = link;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await api.get(`${api.getMyPageBlockEndpoint(block.id)}/data`, { params });
            setData(response.data.result);
        } catch (err) {
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    }, [kedo, block, dossier, link, enabled]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        loading,
        error,
        refetch: fetchData,
    };
};

interface UseDateStatisticsOptions {
    kedo: Kedo;
    block: Block;
    dossier?: { id: number };
    link?: number;
    enabled?: boolean;
}

export interface DefFieldList {
    id: number;
    color?: string;
    [key: string]: unknown;
}

export interface ListFieldData {
    def_field: {
        def_field_lists: DefFieldList[];
    };
}

interface UseDateStatisticsResult {
    data: unknown[] | null;
    listField: ListFieldData | null;
    loading: boolean;
    error: Error | null;
    dateFormat: string;
    setDateFormat: (format: string) => void;
    refetch: () => Promise<void>;
}

export const useDateStatistics = ({
    kedo,
    block,
    dossier,
    link,
    enabled = true,
}: UseDateStatisticsOptions): UseDateStatisticsResult => {
    const [data, setData] = useState<unknown[] | null>(null);
    const [listField, setListField] = useState<ListFieldData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [dateFormat, setDateFormat] = useState(block.settings?.date_format ?? 'year_month');

    const fetchData = useCallback(async () => {
        const environment = kedo.env().getEnvironment();
        if (!environment || !enabled || !block.id) return;

        const api = kedo.api();
        const params: Record<string, unknown> = {
            date_format: dateFormat,
        };

        if (dossier?.id) {
            params.dossier = dossier.id;
            params.linkId = link;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await api.get(`${api.getMyPageBlockEndpoint(block.id)}/data`, { params });

            if (block.settings?.list_field) {
                const dddfResponse = await api.getCached(
                    api.getDefDossierDefFieldEndpoint(block.settings.list_field)
                );
                setListField(dddfResponse.data);
            }

            setData(response.data);
        } catch (err) {
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    }, [kedo, block, dossier, link, dateFormat, enabled]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        listField,
        loading,
        error,
        dateFormat,
        setDateFormat,
        refetch: fetchData,
    };
};
